import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import {
    projectHistoryItemArraySchema,
    projectHistoryItemSchema,
} from '../../../../../shared/source/data-contract/project-history';
import { getUserHardSoftSkillsQueryKey } from '../../../../../shared/source/query-hooks/hard-soft-skills';
import { nullifyEmptyStringValues } from '../../common/form';

import type {
    ProjectHistoryCreation,
    ProjectHistoryItem,
    ProjectHistoryMutation,
} from '../../../../../shared/source/data-contract/project-history';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const useProjectHistoryQuery = (
    userId: string,
    enabled: boolean,
): UseQueryResult<readonly ProjectHistoryItem[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const projectHistoryUrl = `/users/${userId}/project-history` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(projectHistoryItemArraySchema, {
                pathname: projectHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [projectHistoryUrl, 'GET', language.code],
    });
};

export const useOneProjectHistoryQuery = (
    userId: string,
    projectHistoryId: string,
    enabled: boolean,
): UseQueryResult<ProjectHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const oneProjectHistoryUrl = `/users/${userId}/project-history/${projectHistoryId}` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(projectHistoryItemSchema, {
                pathname: oneProjectHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [oneProjectHistoryUrl, 'GET', language.code],
    });
};

export type ProjectHistoryMutations = {
    readonly createProjectMutation: UseMutationResult<ProjectHistoryItem, unknown, Omit<ProjectHistoryItem, 'id'>>;
    readonly deleteProjectMutation: UseMutationResult<void, unknown, string>;
    readonly patchProjectMutation: UseMutationResult<ProjectHistoryItem, unknown, ProjectHistoryItem>;
};

export const useProjectHistoryMutations = (userId: string): ProjectHistoryMutations => {
    const queryClient = useQueryClient();
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const invalidateProjectCache = async (projectHistoryId: Maybe<string>): Promise<void> => {
        if (projectHistoryId.isJust) {
            await queryClient.invalidateQueries({
                queryKey: [`/users/${userId}/project-history/${projectHistoryId}`, 'GET'],
            });
        }
        await queryClient.invalidateQueries({ queryKey: [`/users/${userId}/project-history`, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createProjectMutation = useMutation({
        mutationFn: async (values: Omit<ProjectHistoryItem, 'id'>): Promise<ProjectHistoryItem> => {
            const { companyName, projectTitle, skills, ...restValues } = values;
            const data: ProjectHistoryCreation = {
                ...cleanDeep(restValues),
                companyName,
                projectTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.postJson(projectHistoryItemSchema, {
                pathname: `/users/${userId}/project-history`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (values: ProjectHistoryItem) => invalidateProjectCache(Maybe.just(values.id)),
    });

    const patchProjectMutation = useMutation({
        mutationFn: async (values: ProjectHistoryItem): Promise<ProjectHistoryItem> => {
            const { id, companyName, projectTitle, skills, ...restValues } = values;
            const data: ProjectHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                companyName,
                projectTitle,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.patchJson(projectHistoryItemSchema, {
                pathname: `/users/${userId}/project-history/${values.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, values) => invalidateProjectCache(Maybe.just(values.id)),
    });

    const deleteProjectMutation: UseMutationResult<void, unknown, string> = useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/project-history/${id}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: () => invalidateProjectCache(Maybe.nothing()),
    });

    return {
        createProjectMutation,
        deleteProjectMutation,
        patchProjectMutation,
    };
};
